import { academyFetch } from "./generated/platformTransport";
import type { OrganizationProgramSelectionPage } from "./generated/organization-catalogue";
import { useEffect, useState } from "react";
import { apiBase, academyRequest } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import type {
  AcademicProgramVersionPage,
  AcademicCurriculumRecord,
  AcademicCohortSummary,
  AcademicCohortPage,
  AcademicCurriculumPage,
  AcademicHistoryPage,
  AcademicProgramVersions,
  AcademicProgramPage,
  AcademicProgramSummary,
  CoursePage,
  CourseSummary,
} from "./generated/academic-catalogue";

export const ACADEMIC_CATALOGUE_CHANGED = "academy-academic-catalogue-changed";
export const isAcademicCourseDirectory = (route: string) => route === "course-catalogue";
export const isAcademicOrganizationPortfolio = (route: string) => route === "learning-programs";
export const isAcademicCurriculumDirectory = (route: string) =>
  ["curriculum-maps", "curriculum-approvals"].includes(route);
export const isAcademicCohortDirectory = (route: string) =>
  ["cohorts-intakes", "learning-cohorts"].includes(route);
export const isAcademicDefinitionDirectory = (route: string) =>
  isAcademicCourseDirectory(route) || route === "program-portfolio";
export const isAcademicDirectory = (route: string) =>
  isAcademicDefinitionDirectory(route) ||
  isAcademicCohortDirectory(route) ||
  isAcademicOrganizationPortfolio(route) ||
  isAcademicCurriculumDirectory(route);

async function readAcademicParents(kind: "program" | "course", signal: AbortSignal) {
  const items: Array<CourseSummary | AcademicProgramSummary> = [],
    seen = new Set<string>();
  let after: string | null = null;
  do {
    const page: CoursePage | AcademicProgramPage = await academyRequest(
      `academic-catalogue/${kind}s${after ? `?after=${encodeURIComponent(after)}` : ""}`,
      undefined,
      "GET",
      true,
      undefined,
      signal,
    );
    items.push(...page.items);
    after = page.nextCursor;
    if (after && seen.has(after)) throw new Error("Academic directory pagination did not advance.");
    if (after) seen.add(after);
  } while (after);
  return items;
}

export async function readAcademicCurricula(
  kind: "program" | "course",
  parentId: string,
  signal: AbortSignal,
  include?: "content" | "quality" | "evidence",
) {
  const items: AcademicCurriculumRecord[] = [],
    seen = new Set<string>();
  let before: string | null = null;
  do {
    const query = new URLSearchParams();
    if (before) query.set("before", before);
    if (include) query.set("include", include);
    const page: AcademicCurriculumPage = await academyRequest(
      `academic-catalogue/${kind}/${encodeURIComponent(parentId)}/curriculum-versions${query.size ? `?${query}` : ""}`,
      undefined,
      "GET",
      true,
      undefined,
      signal,
    );
    if (include && page.items.some(item => !item[include]))
      throw new Error("The academic projection is incomplete. Refresh after the service update.");
    items.push(...page.items);
    before = page.nextCursor;
    if (before && seen.has(before))
      throw new Error("Academic version pagination did not advance. Refresh to retry.");
    if (before) seen.add(before);
  } while (before);
  return items.sort(
    (left, right) =>
      Date.parse(right.createdAt) - Date.parse(left.createdAt) || right.id.localeCompare(left.id),
  );
}

export async function readAcademicProgramVersions(
  programId: string,
  signal: AbortSignal,
): Promise<AcademicProgramVersions> {
  const items: AcademicProgramVersions["items"] = [],
    seen = new Set<string>();
  let before: string | null = null,
    first: AcademicProgramVersionPage | undefined;
  do {
    const page: AcademicProgramVersionPage = await academyRequest(
      `academic-catalogue/programs/${encodeURIComponent(programId)}/version-history${before ? `?before=${encodeURIComponent(before)}` : ""}`,
      undefined,
      "GET",
      true,
      undefined,
      signal,
    );
    if (first && (first.version !== page.version || first.currentVersionId !== page.currentVersionId))
      throw new Error("The Program changed while its history was loading. Refresh before editing.");
    first ??= page;
    items.push(...page.items);
    before = page.nextCursor;
    if (before && seen.has(before)) throw new Error("Program history pagination did not advance.");
    if (before) seen.add(before);
  } while (before);
  return {
    programId: first!.programId,
    version: first!.version,
    currentVersionId: first!.currentVersionId,
    items,
  };
}

export function useAcademicRead<T>(
  path: string,
  enabled: boolean,
  load: (signal: AbortSignal) => Promise<T>,
) {
  const account = useIdentity();
  const session = account.session;
  const context = session ? `${session.tenantId}:${session.user.id}:${session.workspace.id}` : "";
  const key = `${context}:${path}`;
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{ key: string; revision: number; data?: T; error: string }>();
  useEffect(() => {
    const refresh = () => setRevision(value => value + 1);
    window.addEventListener(ACADEMIC_CATALOGUE_CHANGED, refresh);
    return () => window.removeEventListener(ACADEMIC_CATALOGUE_CHANGED, refresh);
  }, []);
  useEffect(() => {
    if (!enabled || !session) return;
    const abort = new AbortController();
    let current = true;
    const assertContext = async () => {
      const actual = await checkIdentitySession();
      if (
        actual.tenantId !== session.tenantId ||
        actual.user.id !== session.user.id ||
        actual.workspace.id !== session.workspace.id
      )
        throw new Error("Your workspace changed. Reload to view its academic catalogue.");
    };
    void (async () => {
      try {
        await assertContext();
        const data = await load(abort.signal);
        await assertContext();
        if (current) setState({ key, revision, data, error: "" });
      } catch (error) {
        if (current)
          setState({
            key,
            revision,
            error: error instanceof Error ? error.message : "Academic records could not be loaded.",
          });
      }
    })();
    return () => {
      current = false;
      abort.abort();
    };
    // The stable URL/context is the read identity. Callers create a loader for that URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, revision]);
  const current = state?.key === key && state.revision === revision ? state : undefined;
  return {
    enabled,
    context,
    data: current?.data,
    loading: enabled && !!context && !current,
    error: enabled
      ? current?.error || (!context ? account.error || "An active Academy account is required." : "")
      : "",
    refresh: () => setRevision(value => value + 1),
  };
}

export function useAcademicDirectory(route: string) {
  const identity = useIdentity();
  const organizationId = identity.access?.memberships.find(
    item => item.workspaceId === identity.session?.workspace.id,
  )?.organizationId;
  const kind = isAcademicCurriculumDirectory(route)
    ? "curriculum"
    : isAcademicOrganizationPortfolio(route)
      ? "selection"
      : isAcademicCohortDirectory(route)
        ? "cohort"
        : isAcademicCourseDirectory(route)
          ? "course"
          : "program";
  const path =
    kind === "selection"
      ? `organizations/${organizationId}/learning/tracks/page`
      : `academic-catalogue/${kind}s`;
  type Selection = {
    id: string;
    name: string;
    code: string;
    ownerName: string;
    updatedAt: string;
    status: string;
    academicProgram: OrganizationProgramSelectionPage["items"][number]["academicProgram"];
  };
  type Curriculum = {
    id: string;
    name: string;
    code: string;
    ownerName: string;
    updatedAt: string;
    status: string;
    parentKind: "program" | "course";
    parentId: string;
    parentVersion: number;
    scope: string;
    evidence: string;
    version: number;
    publicationId: string | null;
    modules: string;
    courses: string;
    outcomes: string;
    prerequisites: string;
  };
  const result = useAcademicRead(path, isAcademicDirectory(route), async signal => {
    const items: Array<
        CourseSummary | AcademicProgramSummary | AcademicCohortSummary | Selection | Curriculum
      > = [],
      seen = new Set<string>();
    let cursor: string | null = null;
    if (kind === "curriculum") {
      for (const parentKind of ["program", "course"] as const)
        for (const parent of await readAcademicParents(parentKind, signal)) {
          const versions = await readAcademicCurricula(parentKind, parent.id, signal, "content");
          for (let offset = 0; offset < versions.length; offset += 4)
            items.push(
              ...(await Promise.all(
                versions.slice(offset, offset + 4).map(async version => {
                  const content = version.content!;
                  return {
                    id: version.id,
                    name: version.title,
                    code: version.versionLabel,
                    ownerName: parent.ownerName,
                    updatedAt: version.createdAt,
                    status: version.publicationId ? "published" : version.state,
                    parentKind,
                    parentId: parent.id,
                    parentVersion: parent.version,
                    version: version.version,
                    publicationId: version.publicationId,
                    scope: `${parent.name} · ${version.versionLabel} · ${content.modules.length} modules · ${content.courses.length} Course references`,
                    evidence: `${content.outcomes.length} defined outcomes · ${content.prerequisites.length} prerequisites`,
                    modules:
                      content.modules
                        .map(module => `${module.code}: ${module.title} (${module.lessons.length} lessons)`)
                        .join("; ") || "None recorded",
                    courses:
                      content.courses
                        .map(course => `${course.id} · Version ${course.courseVersionId}`)
                        .join("; ") || "None recorded",
                    outcomes:
                      content.outcomes.map(outcome => `${outcome.code}: ${outcome.title}`).join("; ") ||
                      "None recorded",
                    prerequisites:
                      content.prerequisites
                        .map(edge => `${edge.kind}: ${edge.dependentId} requires ${edge.requiredId}`)
                        .join("; ") || "None recorded",
                  };
                }),
              )),
            );
        }
      return { items, hasMore: false };
    }
    if (kind === "selection") {
      if (!organizationId) throw new Error("A current Organization membership is required.");
      do {
        const page: OrganizationProgramSelectionPage = await academyFetch<OrganizationProgramSelectionPage>(
          `${apiBase}/v1/${path}${cursor ? `?after=${encodeURIComponent(cursor)}` : ""}`,
          { credentials: "include", cache: "no-store", signal },
        );
        items.push(
          ...page.items.map(item => ({
            id: item.id,
            name: item.name,
            code: item.schemaVersion,
            ownerName: identity.session?.workspace.name || "Current Organization",
            updatedAt: item.updatedAt,
            status: item.state,
            academicProgram: item.academicProgram,
          })),
        );
        cursor = page.nextCursor;
        if (cursor && seen.has(cursor)) throw new Error("Program portfolio pagination did not advance.");
        if (cursor) seen.add(cursor);
      } while (cursor);
      return { items, hasMore: false };
    }
    do {
      const page: CoursePage | AcademicProgramPage | AcademicCohortPage = await academyRequest<
        CoursePage | AcademicProgramPage | AcademicCohortPage
      >(
        `${path}${cursor ? `?after=${encodeURIComponent(cursor)}` : ""}`,
        undefined,
        "GET",
        true,
        undefined,
        signal,
      );
      items.push(...page.items);
      cursor = page.nextCursor;
      if (cursor && seen.has(cursor))
        throw new Error("Academic pagination did not advance. Refresh the catalogue.");
      if (cursor) seen.add(cursor);
    } while (cursor && items.length < 500);
    return { items, hasMore: !!cursor };
  });
  const rows = (result.data?.items || []).map(item => ({
    id: item.id,
    name: item.name,
    code: "code" in item ? item.code : item.id,
    owner: "ownerName" in item ? item.ownerName : "Current entity",
    updated: new Date(item.updatedAt).toLocaleString(),
    status: item.status,
    ...("parentKind" in item
      ? {
          parentKind: item.parentKind,
          parentId: item.parentId,
          parentVersion: item.parentVersion,
          version: item.version,
          publicationId: item.publicationId || "",
          scope: item.scope,
          evidence: item.evidence,
          modules: item.modules,
          courses: item.courses,
          outcomes: item.outcomes,
          prerequisites: item.prerequisites,
        }
      : {}),
    ...("academicProgram" in item
      ? {
          name: item.academicProgram?.name || `${item.name} · Program unavailable`,
          selection: item.name,
          programVersionId: item.academicProgram?.versionId || "Unavailable",
          curriculumVersionId: item.academicProgram?.curriculumVersionId || "Unavailable",
          scope: "Organization selection",
          people: "Not reported",
          progress: "Not reported",
        }
      : {}),
    ...(kind === "cohort" && "startAt" in item
      ? {
          program: item.programName || "Unresolved academic version",
          programId: item.programId,
          programVersionId: item.programVersionId || "Not resolved",
          curriculumVersionId: item.curriculumVersionId || "Not resolved",
          scope: item.privateCohort ? "Private cohort" : "Public cohort",
          capacity: item.capacity,
          startAt: item.startAt,
          endAt: item.endAt,
          deadline: item.applicationDeadlineAt
            ? new Date(item.applicationDeadlineAt).toLocaleString()
            : "Not configured",
          timezone: item.timezone,
          language: item.language,
          deliveryMode: item.deliveryMode,
          progress: "Not reported",
          people: "Not reported",
          version: item.version,
          resolutionStatus: item.resolutionStatus,
        }
      : {}),
    ...(kind === "program"
      ? {
          category: "category" in item ? item.category || "Not provided" : "Not provided",
          scope: "Institution",
          people: "Not available",
          progress: "Not available",
        }
      : {}),
  }));
  return { ...result, kind, rows };
}

export function useAcademicHistory(route: string, parentId?: string, parentKind?: string) {
  const path = `academic-catalogue/${isAcademicCohortDirectory(route) ? "cohorts" : isAcademicCourseDirectory(route) || parentKind === "course" ? "course" : "program"}/${encodeURIComponent(parentId || "")}/history`;
  const result = useAcademicRead(
    path,
    (isAcademicDefinitionDirectory(route) ||
      isAcademicCohortDirectory(route) ||
      isAcademicCurriculumDirectory(route)) &&
      !!parentId,
    signal => academyRequest<AcademicHistoryPage>(path, undefined, "GET", true, undefined, signal),
  );
  return { ...result, items: result.data?.items || [] };
}
export type AcademicDirectory = ReturnType<typeof useAcademicDirectory>;

export function useAcademicLifecycle(route: string, id?: string) {
  return useAcademicRead(
    `lifecycle:${route}:${id}`,
    isAcademicDefinitionDirectory(route) && !!id,
    async signal => {
      if (isAcademicCourseDirectory(route)) {
        const versions = await readAcademicCurricula("course", id!, signal);
        const pending = versions.find(
          item => item.state === "in_review" || (item.state === "approved" && !item.publicationId),
        );
        return {
          canReview: pending?.state === "in_review",
          canPublish: pending?.state === "approved" && !pending?.publicationId,
        };
      }
      const versions = await readAcademicProgramVersions(id!, signal);
      return {
        canReview: versions.items.some(item => item.status === "in_review"),
        canPublish: versions.items.some(item => item.status === "approved"),
      };
    },
  );
}
